import moment from 'moment';
import { calculateShifts, findShiftForPatrol } from './shiftCalculator';

/**
 * Shift Rescheduler Utility
 *
 * Classifies detected conflicts as pushback-eligible (same shift, subsequent patrol)
 * or generic, and provides the ability to reschedule subsequent patrols to resolve overlaps.
 */

/**
 * Classifies conflicts from detectConflicts into pushback-eligible and generic.
 *
 * Pushback-eligible: the conflicting patrol is in the same shift as the edited patrol
 * AND comes after it in the shift order (i.e. the edited patrol was extended into it).
 *
 * @param {Object} editedPatrol - The patrol with updated times (must have ScheduleAssignmentId)
 * @param {Array} conflicts - Raw conflict array from detectConflicts
 * @param {Array} existingPatrols - All existing patrols (used to compute shifts)
 * @returns {Object} { pushbackConflicts, genericConflicts, overlapInfo }
 *   overlapInfo has the shape ShiftOverlapDialog expects when pushbackConflicts exist, null otherwise
 */
export const classifyConflicts = (editedPatrol, conflicts, existingPatrols) => {
  const allGeneric = () => ({
    pushbackConflicts: [],
    genericConflicts: conflicts ? [...conflicts] : [],
    overlapInfo: null,
  });

  if (!conflicts || conflicts.length === 0) {
    return allGeneric();
  }

  // Only classify in edit mode (must have an ID)
  if (!editedPatrol || !editedPatrol.ScheduleAssignmentId) {
    return allGeneric();
  }

  // Find the original patrol in existing patrols
  const originalPatrol = existingPatrols.find(
    (p) => p.ScheduleAssignmentId === editedPatrol.ScheduleAssignmentId
  );
  if (!originalPatrol) {
    return allGeneric();
  }

  // Guard name changed — not the same shift scenario, all generic
  if (editedPatrol.ContactName !== originalPatrol.ContactName) {
    return allGeneric();
  }

  // Build shifts from existing patrols (using the originals, not the edited version)
  const shifts = calculateShifts(existingPatrols);

  // Find the shift containing this patrol
  const shift = findShiftForPatrol(shifts, originalPatrol);
  if (!shift) {
    return allGeneric();
  }

  // Get sorted patrols in this shift
  const shiftPatrols = [...shift.patrols].sort((a, b) =>
    moment(a.StartTime).diff(moment(b.StartTime))
  );

  // Find the index of the edited patrol within the shift
  const patrolIndex = shiftPatrols.findIndex(
    (p) => p.ScheduleAssignmentId === editedPatrol.ScheduleAssignmentId
  );

  // Last patrol in shift or not found — no subsequent patrols, all generic
  if (patrolIndex === -1 || patrolIndex === shiftPatrols.length - 1) {
    return allGeneric();
  }

  // Collect IDs of patrols that come AFTER the edited one in the shift
  const subsequentPatrols = shiftPatrols.slice(patrolIndex + 1);
  const subsequentIds = new Set(subsequentPatrols.map((p) => p.ScheduleAssignmentId));

  // Classify each conflict
  const pushbackConflicts = [];
  const genericConflicts = [];

  for (const conflict of conflicts) {
    if (
      conflict.conflictingPatrol &&
      subsequentIds.has(conflict.conflictingPatrol.ScheduleAssignmentId)
    ) {
      pushbackConflicts.push(conflict);
    } else {
      genericConflicts.push(conflict);
    }
  }

  // Build overlapInfo if there are pushback-eligible conflicts
  let overlapInfo = null;
  if (pushbackConflicts.length > 0) {
    const nextPatrol = shiftPatrols[patrolIndex + 1];
    const editedEnd = moment(editedPatrol.EndTime);
    const nextStart = moment(nextPatrol.StartTime);
    const overlapMinutes = editedEnd.diff(nextStart, 'minutes');

    overlapInfo = {
      editedPatrol,
      originalPatrol,
      subsequentPatrols,
      overlapMinutes: Math.max(overlapMinutes, 0),
    };
  }

  return { pushbackConflicts, genericConflicts, overlapInfo };
};

/**
 * Reschedules subsequent patrols to resolve an overlap.
 *
 * @param {Object} editedPatrol - The patrol that was edited (with new times)
 * @param {Object} originalPatrol - The original version of the edited patrol
 * @param {Array} subsequentPatrols - Patrols after the edited one in the shift
 * @param {boolean} preserveGaps - If true, keep original gaps between patrols; if false, remove gaps
 * @returns {Array} Array of modified patrol objects (only those that actually changed)
 */
export const rescheduleSubsequentPatrols = (editedPatrol, originalPatrol, subsequentPatrols, preserveGaps) => {
  if (!subsequentPatrols || subsequentPatrols.length === 0) {
    return [];
  }

  const modified = [];
  // The "previous" patrol starts as the edited one
  let previousEnd = moment(editedPatrol.EndTime);
  // For preserveGaps, we need the original previous end to compute original gaps
  let previousOriginalEnd = moment(originalPatrol.EndTime);

  for (let i = 0; i < subsequentPatrols.length; i++) {
    const patrol = subsequentPatrols[i];
    const patrolStart = moment(patrol.StartTime);
    const patrolEnd = moment(patrol.EndTime);
    const duration = patrolEnd.diff(patrolStart, 'minutes');

    let newStart;

    if (preserveGaps) {
      // Keep the original gap from the previous patrol
      const originalGap = patrolStart.diff(previousOriginalEnd, 'minutes');
      newStart = previousEnd.clone().add(originalGap, 'minutes');
    } else {
      // Remove gaps: only move if there's an overlap
      if (previousEnd.isAfter(patrolStart)) {
        newStart = previousEnd.clone();
      } else {
        // No overlap with previous — stop cascading
        break;
      }
    }

    const newEnd = newStart.clone().add(duration, 'minutes');

    // Stop cascading if the patrol would cross into a different date (midnight boundary)
    if (!newStart.isSame(moment(patrol.StartTime), 'day')) {
      break;
    }

    // Only include if times actually changed
    if (!newStart.isSame(patrolStart) || !newEnd.isSame(patrolEnd)) {
      modified.push({
        ...patrol,
        StartTime: newStart.toDate(),
        EndTime: newEnd.toDate(),
      });
    }

    // Update tracking for next iteration
    previousEnd = newEnd;
    previousOriginalEnd = patrolEnd;
  }

  return modified;
};
